import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { ValidationController, validationMessages } from "aurelia-validation";
import * as moment from "moment";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { EntityFinderSearch } from "../../../../../components/entity-finder/entity-finder-search";
import { BaseVetOrder } from "../base-vet-order";
import { Reflection } from "../../../../../../utilities/reflection";
import { OrderModel } from "../../../../../../models/rightstore/rightstore.models";

@autoinject()
@transient()
@useView('views/pages/modules/orders/plugins/trade-check/trade-check.html')
export class TradeCheck extends BaseVetOrder {

    public portEntityTypes: EntityTypeConfigurationModel[];
    public vesselEntityTypes: EntityTypeConfigurationModel[] = this.getVesselSearchConfig();
    validator: ClientSideValidator;
    _manualLoadPort: boolean = false;
    portFromId: number;
    public voyageFromVM: EntityFinderSearch;
    public vesselVM: EntityFinderSearch;
    public unknownPortFrom: boolean = false;
    public sanctionedPortFrom: boolean = false;
    _manualDischargePort: boolean = false;
    portToId: number;
    public voyageToVM: EntityFinderSearch;
    public unknownPortTo: boolean = false;
    public sanctionedPortTo: boolean = false;
    unverifiedRestrictionsPortCodes = ["DS00007", "GENERIC-002", "GENERIC-004"];
    departureDate: Date;
    loadPortCountryId: number;
    loadPort: string;
    arrivalDate: Date;
    dischargePortCountryId: number;
    dischargePort: string;
    cargoTypeId: number;
    cargoTypeValue: LookupValue; 
    berth: string;
    public charterTypeId: number;
    public timeCharterStartDate?: Date;
    public timeCharterEndDate?: Date;
    public confirmNonSanctionedCountry: boolean = false;
    public confirmSanctionedCountry: boolean = false;
    public canSubmit: boolean = true;
    public timeCharterMinEndDate: Date;


    activate(params: { vesselId: number, order: OrderModel, viewTitle: string }) {
        this.vesselId = params.vesselId;
        this.canEditVessel = !params.vesselId || params.order?.orderGuid != null;
        if (params.viewTitle != null && params.viewTitle != "") this.viewTitle = params.viewTitle;
    }

    attached() {
        if (this.vesselId) {
            this.vesselVM.set({ entityTypeUri: this.vesselEntityTypes[0].uri, id: this.vesselId }).then(() => {
                this.setVessel(this.vesselVM.selectedEntity);
            });
        }
    }

    @computedFrom("sanctionedPorts", "confirmNonSanctionedCountry", "sanctionedPorts", "confirmSanctionedCountry", "charterTypeId", "timeCharterEndDate")
    protected get disableSubmitInternal(): boolean {
        return (!this.sanctionedPorts && !this.confirmNonSanctionedCountry) || 
            (this.sanctionedPorts && !this.confirmSanctionedCountry);
    }

    @computedFrom(Reflection.getPropertyPath<TradeCheck>(z => z._manualLoadPort))
    get manualLoadPort() {
        return this._manualLoadPort
    }

    @computedFrom(Reflection.getPropertyPath<TradeCheck>(z => z._manualDischargePort))
    get manualDischargePort() {
        return this._manualDischargePort
    }

    set manualLoadPort(x: boolean) {
        this._manualLoadPort = x;
        if (this._manualLoadPort) {
            this.voyageFromVM.removeFilter();
        }
    }

    set manualDischargePort(x: boolean) {
        this._manualDischargePort = x;
        if (this._manualDischargePort) {
            this.voyageToVM.removeFilter();
        }
    }

    @computedFrom("sanctionedPortTo", "sanctionedPortFrom")
    public get sanctionedPorts(): boolean {
        return this.sanctionedPortTo || this.sanctionedPortFrom;
    }

    @computedFrom("cargoTypeValue")
    get isOtherCargo() {
        return this.cargoTypeValue ? this.cargoTypeValue.additionalData['code'] == 'OTH' : false;
    }

    viewTitle: string = "Trade Check";

    constructor(protected vesselService: ManageVesselService, private validationController: ValidationController,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private entityValidationsService: EntityValidationsService) {
        super(vesselService);
        this.validationController.addRenderer(new CustomValidationRenderer());
        validationMessages['required'] = `\${$displayName} is required.`;
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.timeCharterMinEndDate = moment().startOf('d').toDate();
    }

    dispose() {
    }

    public onVoyageFromSelected(port) {
        this.portFromId = port ? port.id : null;

        this.unknownPortFrom = port && (port.countryCode == "UNK" || this.unverifiedRestrictionsPortCodes.indexOf(port.portCode) > -1);
        this.sanctionedPortFrom = !this.unknownPortFrom && port && (port.isSanctioned && port.isSanctioned.toString().toLowerCase() == 'true');
    }

    public manualLoadPortCheckChanged() {
        this.sanctionedPortFrom = false;
        this.unknownPortFrom = false;
    }

    public manualLoadPortChanged(model: any) {
        this.sanctionedPortFrom = model.additionalData.isSanctioned;
    }

    public onVoyageToSelected(port) {
        this.portToId = port ? port.id : null;

        this.unknownPortTo = port && (port.countryCode == "UNK" || this.unverifiedRestrictionsPortCodes.indexOf(port.portCode) > -1);
        this.sanctionedPortTo = !this.unknownPortTo && port && (port.isSanctioned && port.isSanctioned.toString().toLowerCase() == 'true');
    }

    public manualDischargePortCheckChanged() {
        this.sanctionedPortTo = false;
        this.unknownPortTo = false;
    }

    public manualDischargePortChanged(model: any) {
        this.sanctionedPortTo = model.additionalData.isSanctioned;
    }

    getVesselSearchConfig(): EntityTypeConfigurationModel[] {
        return this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["VesselSearch"]).filter(x => x.key == "BasicVesselSearch");
    }

    private setValidator() {
        const rules = [{
            errorMessage: "Vessel Name / IMO is required",
            parameters: null, property: "vesselId",
            validationType: ValidationTypeEnum.Required
        }];

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Screening Request",
            isCollection: false,
            nestedRulesHolder: [],
            rules: rules
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    today() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    estDepartureMinDate() {
        if (this.departureDate) {
            return this.departureDate && moment(this.departureDate).isAfter(moment(), 'd') ?
                new Date(this.departureDate) : this.today();
        }
        else return null;
    }

    estArrivalMinDate() {
        if (this.arrivalDate) {
            return this.arrivalDate && moment(this.arrivalDate).isAfter(moment(), 'd') ?
                new Date(this.arrivalDate) : this.today();
        }
        else return null;
    }

    public async isValid(): Promise<boolean> {
        this.setValidator();
        return await this.validator.validate().then(x => {
            return x.valid
        });
    }

    public getMetaModel() {
        const metaModel = {
            vesselId: this.vesselId,
            portFromId: this.portFromId,
            portToId: this.portToId,
            departureDate: this.departureDate,
            arrivalDate: this.arrivalDate,
            cargoTypeId: this.cargoTypeId,
            berth: this.berth,
            manualDischargePort: this.manualDischargePort,
            manualLoadPort: this.manualLoadPort,
            dischargePortCountryId: this.dischargePortCountryId,
            loadPortCountryId: this.loadPortCountryId,
            dischargePort: this.dischargePort,
            loadPort: this.loadPort,
            charterTypeId: this.charterTypeId,
            timeCharterStartDate: this.timeCharterStartDate,
            timeCharterEndDate: this.timeCharterEndDate
        };

        return metaModel;
    }

}